import { Box } from '@mui/material';
import { Contact } from 'model/contacts.model';
import { LayoutType } from 'model/deals.model';
import { useCallback, useEffect, useState } from 'react';
import { ContactService } from 'service/contact.service';
import ContactsGrid from '../ContactsGrid';
import { ContactsTable } from '../ContactsTable';

type BusinessContactsProps = {
  layout: LayoutType;
  search?: string;
};

const BusinessContacts = ({ layout, search }: BusinessContactsProps) => {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [sortBy, setSortBy] = useState<string | undefined>(undefined);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc' | undefined>(undefined);

  const fetchContacts = useCallback(async () => {
    try {
      setLoading(true);
      const response = await ContactService.getBusinessContacts({
        page: currentPage + 1,
        limit: rowsPerPage,
        ...(search?.trim() ? { search: search.trim() } : {}),
        ...(sortBy ? { sortBy } : {}),
        ...(sortOrder ? { sortOrder } : {}),
      });
      const list = response?.contacts ?? [];
      setContacts(list);
      setTotalCount(typeof response?.total === 'number' ? response.total : list.length);
    } catch (err) {
      console.error(err);
      setContacts([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, rowsPerPage, search, sortBy, sortOrder]);

  useEffect(() => {
    void fetchContacts();
  }, [fetchContacts]);

  const handleAddToMyContacts = useCallback(
    async (contact: Contact) => {
      const payload = ContactService.buildAddToContactsPayload(contact.businessId, {
        name: contact.name,
        email: contact.email,
        phone: contact.phone,
        roles: contact.roles,
        department: contact.department,
        company: contact.company,
        profileImageUrl: contact.profileImageUrl,
        profilePicture: contact.profilePicture,
        zoominfoPersonId: contact.zoominfoPersonId,
      });
      if (!payload) {
        console.error('Add to contacts requires businessId and at least name or email');
        return;
      }
      await ContactService.addToContacts(payload);
      await fetchContacts();
    },
    [fetchContacts],
  );

  return (
    <Box sx={{ width: '100%', flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      {layout === LayoutType.KANBAN ? (
        <ContactsGrid
          variant='business'
          data={contacts}
          isLoading={loading}
          onRefetch={fetchContacts}
        />
      ) : (
        <ContactsTable
          variant='business'
          contacts={contacts}
          rowsPerPage={rowsPerPage}
          setRowsPerPage={(n) => {
            setRowsPerPage(n);
            setCurrentPage(0);
          }}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          totalCount={totalCount}
          isLoading={loading}
          onSort={(field, order) => {
            setCurrentPage(0);
            setSortBy(field);
            setSortOrder(order);
          }}
          onAddToMyContacts={handleAddToMyContacts}
        />
      )}
    </Box>
  );
};

export default BusinessContacts;
